import { useEffect, useState } from 'react';
import {
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  Table,
  Badge,
  Loader,
  Alert,
  Paper,
  SimpleGrid,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconPlayerPlay,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconUsers,
  IconClock,
  IconCurrencyDollar,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

import { getAggregatedSlots, generatePredictions } from '../api/prediction-api';
import type { AggregatedSlot, GeneratePredictionsResponse } from '../types/prediction-types';
import PredictionChart from '../components/PredictionChart';
import HourlyRevenueChart from '../components/HourlyRevenueChart';

export default function PredictionsPage() {
  // Form state
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(
    dayjs().add(30, 'day').toDate()
  );

  // Data state
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slots, setSlots] = useState<AggregatedSlot[]>([]);

  const fetchSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { limit: '500' };
      if (startDate) params.start_date = dayjs(startDate).format('YYYY-MM-DD');
      if (endDate) params.end_date = dayjs(endDate).format('YYYY-MM-DD');

      const response = await getAggregatedSlots(params);
      setSlots(response.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load predictions');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const request = {
        start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
        end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      };

      const response: GeneratePredictionsResponse = await generatePredictions(request);

      if (response.predictions.success) {
        notifications.show({
          title: 'Predictions Generated',
          message: `Generated ${response.predictions.slots_predicted} slot predictions for ${response.predictions.games_predicted} games`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        // Refresh the slots
        await fetchSlots();
      } else {
        throw new Error(response.predictions.errors?.join(', ') || 'Generation failed');
      }
    } catch (err) {
      notifications.show({
        title: 'Generation Failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  // Group slots by date for display
  const groupedByDate = slots.reduce((acc, slot) => {
    const date = slot.slot_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(slot);
    return acc;
  }, {} as Record<string, AggregatedSlot[]>);

  const sortedDates = Object.keys(groupedByDate).sort();

  // Calculate totals
  const totalRevenue = slots.reduce((sum, s) => sum + s.predicted_revenue, 0);
  const totalPrepTime = slots.reduce((sum, s) => sum + s.predicted_preparation_time, 0);
  const maxEmployees = slots.length > 0 ? Math.max(...slots.map(s => s.employees_needed)) : 0;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Predictions</Title>
          <Text c="dimmed" size="sm">
            Generate and view sales predictions for upcoming games
          </Text>
        </div>
      </Group>

      {/* Controls */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Text fw={600}>Prediction Settings</Text>
          <Group grow>
            <DatePickerInput
              label="Start Date"
              placeholder="Pick start date"
              value={startDate}
              onChange={setStartDate}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Pick end date"
              value={endDate}
              onChange={setEndDate}
              clearable
            />
          </Group>
          <Group>
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={handleGenerate}
              loading={generating}
            >
              Generate Predictions
            </Button>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={fetchSlots}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Stack align="center" py="xl">
          <Loader size="md" />
          <Text c="dimmed">Loading predictions...</Text>
        </Stack>
      )}

      {/* Results */}
      {!loading && !error && (
        <>
          {/* Summary Cards */}
          <Group grow>
            <Paper shadow="xs" p="md" withBorder>
              <Group>
                <IconCurrencyDollar size={24} color="#40c057" />
                <div>
                  <Text size="xs" c="dimmed">Total Revenue</Text>
                  <Text size="lg" fw={700}>${totalRevenue.toFixed(2)}</Text>
                </div>
              </Group>
            </Paper>
            <Paper shadow="xs" p="md" withBorder>
              <Group>
                <IconClock size={24} color="#fab005" />
                <div>
                  <Text size="xs" c="dimmed">Total Prep Time</Text>
                  <Text size="lg" fw={700}>{totalPrepTime.toFixed(0)} min</Text>
                </div>
              </Group>
            </Paper>
            <Paper shadow="xs" p="md" withBorder>
              <Group>
                <IconUsers size={24} color="#228be6" />
                <div>
                  <Text size="xs" c="dimmed">Max Staff Needed</Text>
                  <Text size="lg" fw={700}>{maxEmployees}</Text>
                </div>
              </Group>
            </Paper>
          </Group>

          <Text size="sm" c="dimmed">
            Showing {slots.length} time slots across {sortedDates.length} days
          </Text>

          {slots.length === 0 ? (
            <Alert color="blue" title="No Predictions">
              No predictions found for the selected date range. Click "Generate Predictions" to create new predictions.
            </Alert>
          ) : (
            <Stack gap="md">
              {/* Charts for each date */}
              {sortedDates.map((date) => {
                const daySlots = groupedByDate[date];
                const dayRevenue = daySlots.reduce((sum, s) => sum + s.predicted_revenue, 0);
                
                return (
                  <Stack key={`charts-${date}`} gap="md">
                    <Group justify="space-between" align="baseline" mt="lg">
                      <Title order={3}>
                        {dayjs(date).format('dddd, MMMM D, YYYY')}
                      </Title>
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">Total Revenue:</Text>
                        <Text size="lg" fw={700} c="green">
                          ${dayRevenue.toFixed(2)}
                        </Text>
                      </Group>
                    </Group>
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                      <HourlyRevenueChart slots={slots} date={date} />
                      <PredictionChart slots={slots} date={date} />
                    </SimpleGrid>
                  </Stack>
                );
              })}

              {/* Detailed tables */}
              <Title order={3} mt="xl">Detailed Time Slots</Title>
              {sortedDates.map((date) => {
                const daySlots = groupedByDate[date];
                const dayRevenue = daySlots.reduce((sum, s) => sum + s.predicted_revenue, 0);
                const dayOrders = daySlots.reduce((sum, s) => sum + s.predicted_orders, 0);
                const dayItems = daySlots.reduce((sum, s) => sum + s.predicted_items, 0);
                const dayPrepTime = daySlots.reduce((sum, s) => sum + s.predicted_preparation_time, 0);
                
                return (
                  <Paper key={date} shadow="xs" p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Text fw={600} size="lg">
                        {dayjs(date).format('dddd, MMMM D, YYYY')}
                      </Text>
                      <Badge color="blue" variant="light">
                        {groupedByDate[date].length} slots
                      </Badge>
                    </Group>

                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Time</Table.Th>
                          <Table.Th>Duration</Table.Th>
                          <Table.Th>Games</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Orders</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Items</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Prep Time</Table.Th>
                          <Table.Th style={{ textAlign: 'center' }}>Staff</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {groupedByDate[date]
                          .sort((a, b) => a.slot_start_time.localeCompare(b.slot_start_time))
                          .map((slot) => {
                            // Parse slot_category to show games info
                            const gameCount = slot.slot_category.split('+').length;
                            
                            return (
                              <Table.Tr key={slot.id}>
                                <Table.Td>
                                  <Text size="sm" fw={500}>
                                    {dayjs(slot.slot_start_time).format('h:mm A')} - {dayjs(slot.slot_end_time).format('h:mm A')}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{slot.duration_minutes.toFixed(0)} min</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge 
                                    color={gameCount > 1 ? 'orange' : 'gray'} 
                                    variant="light" 
                                    size="sm"
                                  >
                                    {gameCount} {gameCount === 1 ? 'game' : 'games'}
                                  </Badge>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Text size="sm" fw={500} c="green">
                                    ${slot.predicted_revenue.toFixed(2)}
                                  </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Text size="sm">{slot.predicted_orders.toFixed(1)}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Text size="sm">{slot.predicted_items.toFixed(1)}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Text size="sm">{slot.predicted_preparation_time.toFixed(1)} min</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'center' }}>
                                  <Badge 
                                    color={slot.employees_needed >= 3 ? 'red' : slot.employees_needed >= 2 ? 'yellow' : 'green'} 
                                    variant="filled"
                                    size="lg"
                                  >
                                    {slot.employees_needed}
                                  </Badge>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                      </Table.Tbody>
                      <Table.Tfoot>
                        <Table.Tr>
                          <Table.Td colSpan={3}>
                            <Text fw={700} size="sm">Daily Total</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text fw={700} size="sm" c="green">
                              ${dayRevenue.toFixed(2)}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text fw={700} size="sm">{dayOrders.toFixed(1)}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text fw={700} size="sm">{dayItems.toFixed(1)}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text fw={700} size="sm">{dayPrepTime.toFixed(1)} min</Text>
                          </Table.Td>
                          <Table.Td></Table.Td>
                        </Table.Tr>
                      </Table.Tfoot>
                    </Table>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
